'use client'

import React, { useState, useEffect } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Skeleton } from '@/components/ui/skeleton'
import { GET_COLLECTIONS_FOR_SELECT } from '../../Collections/collectionsQueries'
import { UPDATE_PRODUCT_COLLECTIONS } from '../productMutations'
import { GET_PRODUCT_DETAILS } from '../productQueries'

interface CollectionEditModalProps {
  isOpen: boolean
  onClose: () => void
  productId: number
  currentCollectionIds: number[]
  language: string
}

export function CollectionEditModal({
  isOpen,
  onClose,
  productId,
  currentCollectionIds,
  language,
}: CollectionEditModalProps) {
  const [selectedCollectionIds, setSelectedCollectionIds] = useState<number[]>([])

  const { data: collectionsData, loading: collectionsLoading } = useQuery(
    GET_COLLECTIONS_FOR_SELECT,
    {
      skip: !isOpen,
    }
  )

  const [updateCollections, { loading: updating }] = useMutation(
    UPDATE_PRODUCT_COLLECTIONS,
    {
      refetchQueries: [
        { query: GET_PRODUCT_DETAILS, variables: { id: productId } },
      ],
      onCompleted: () => {
        onClose()
      },
    }
  )

  useEffect(() => {
    if (isOpen) {
      setSelectedCollectionIds(currentCollectionIds)
    }
  }, [isOpen, currentCollectionIds])

  const handleToggleCollection = (collectionId: number) => {
    setSelectedCollectionIds(prev =>
      prev.includes(collectionId)
        ? prev.filter(id => id !== collectionId)
        : [...prev, collectionId]
    )
  }

  const handleSave = async () => {
    try {
      await updateCollections({
        variables: {
          id: productId,
          collections: selectedCollectionIds.map(id => ({ id })),
        },
      })
    } catch (error) {
      console.error('Error updating collections:', error)
    }
  }

  const handleClose = () => {
    setSelectedCollectionIds(currentCollectionIds)
    onClose()
  }

  const getCollectionName = (collection: any) => {
    return (
      collection.collection_profiles.find(
        (profile: any) => profile.language.toLowerCase() === language
      )?.name || collection.collection_profiles[0]?.name
    )
  }

  const collections = collectionsData?.findManyCollection || []

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit Collections</DialogTitle>
          <DialogDescription>
            Select which collections this product belongs to.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {collectionsLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center space-x-3">
                  <Skeleton className="h-4 w-4" />
                  <Skeleton className="h-4 w-32" />
                </div>
              ))}
            </div>
          ) : collections.length === 0 ? (
            <p className="text-sm text-gray-500">No collections available.</p>
          ) : (
            <div className="grid grid-cols-1 gap-2 max-h-80 overflow-y-auto">
              {collections.map((collection: any) => (
                <div
                  key={collection.id}
                  className="flex items-center space-x-3 p-2 rounded hover:bg-gray-50"
                >
                  <Checkbox
                    id={`collection-${collection.id}`}
                    checked={selectedCollectionIds.includes(collection.id)}
                    onCheckedChange={() => handleToggleCollection(collection.id)}
                  />
                  <label
                    htmlFor={`collection-${collection.id}`}
                    className="text-sm font-medium leading-none cursor-pointer"
                  >
                    {getCollectionName(collection)}
                  </label>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={updating || collectionsLoading}>
            {updating ? (
              <div className="flex items-center gap-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                Saving...
              </div>
            ) : (
              'Save'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
